import React, { useState, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slider,
  FormControlLabel,
  Checkbox,
  Switch,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Send,
  Campaign,
  NotificationsActive,
  Warning,
  CheckCircle,
  Error,
  Group,
  Person,
  Pause,
  PlayArrow,
  Stop,
  Delete,
  PriorityHigh,
  Notifications,
  CloudUpload,
  Image,
  Close,
  Videocam,
  Gif,
  AccountTree,
  Search,
  ContentPaste
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { adminAPI } from '../services/api';
import { formatNumber } from '../utils/formatters';

// Модули и форматы для Quill редактора
const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    ['link'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['clean']
  ],
  clipboard: {
    matchVisual: false,
    // Разрешаем все HTML теги при вставке
    allowed: {
      tags: ['a', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'br', 'p', 'span'],
      attributes: {
        a: ['href', 'target'],
        span: ['style']
      }
    }
  },
};

const quillFormats = [
  'bold', 'italic', 'underline', 'strike',
  'link', 'list', 'bullet'
];

// Конвертация HTML в Telegram-совместимый формат для превью
const convertHtmlToTelegramPreview = (html) => {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<p>/gi, '')
    .replace(/<\/p>/gi, '\n')
    .replace(/<strong>/gi, '<b>')
    .replace(/<\/strong>/gi, '</b>')
    .replace(/<em>/gi, '<i>')
    .replace(/<\/em>/gi, '</i>')
    .replace(/<u>/gi, '<u>')
    .replace(/<\/u>/gi, '</u>')
    .replace(/<s>/gi, '<s>')
    .replace(/<\/s>/gi, '</s>')
    .replace(/<del>/gi, '<s>')
    .replace(/<\/del>/gi, '</s>')
    .replace(/<span[^>]*>/gi, '')
    .replace(/<\/span>/gi, '')
    .replace(/<ul>/gi, '')
    .replace(/<\/ul>/gi, '')
    .replace(/<ol>/gi, '')
    .replace(/<\/ol>/gi, '')
    .replace(/<li>/gi, '• ')
    .replace(/<\/li>/gi, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const PushAlerts = () => {
  const [loading, setLoading] = useState(false);
  const [sendingStatus, setSendingStatus] = useState(null);

  // Параметры рассылки
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('high'); // high, normal, low - по умолчанию высокий
  const [targetType, setTargetType] = useState('all');
  const [targetUser, setTargetUser] = useState('');
  const [targetGroup, setTargetGroup] = useState('active');
  const [deliveryChannel, setDeliveryChannel] = useState('both');
  const [includeButtons, setIncludeButtons] = useState(false);
  const [buttonText, setButtonText] = useState('');
  const [buttonUrl, setButtonUrl] = useState('');

  // Параметры для рассылки по структуре
  const [structureUser, setStructureUser] = useState(null);
  const [structureUserId, setStructureUserId] = useState(null);
  const [structureDepth, setStructureDepth] = useState(5);
  const [structurePreview, setStructurePreview] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  // Фильтры для структуры
  const [filterHasStaking, setFilterHasStaking] = useState(false);
  const [filterNoReferrals, setFilterNoReferrals] = useState(false);

  // Медиа (только для Telegram)
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [mediaType, setMediaType] = useState(null); // 'photo', 'video', 'animation'
  const [mediaFileId, setMediaFileId] = useState(null);
  const [mediaLocalPath, setMediaLocalPath] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState(null);

  // Настройки защиты от спама
  const [batchSize, setBatchSize] = useState(30);
  const [delayBetweenBatches, setDelayBetweenBatches] = useState(1000);
  const [delayBetweenMessages, setDelayBetweenMessages] = useState(50);
  const [testMode, setTestMode] = useState(false);

  // Статистика
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    sentToday: 0,
    sentThisWeek: 0,
    sentThisMonth: 0,
    queueLength: 0,
    processing: false,
    lastSent: null
  });

  // История рассылок
  const [history, setHistory] = useState([]);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRowsPerPage, setHistoryRowsPerPage] = useState(10);
  const [historyTotal, setHistoryTotal] = useState(0);

  // Очередь рассылки
  const [queue, setQueue] = useState({
    status: 'idle',
    total: 0,
    sent: 0,
    failed: 0,
    remaining: 0,
    currentBroadcastId: null
  });

  // Диалог подтверждения очистки истории
  const [clearHistoryDialog, setClearHistoryDialog] = useState(false);

  useEffect(() => {
    loadStats();
    loadHistory();
    const interval = setInterval(() => {
      if (queue.status === 'processing') {
        loadQueueStatus();
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [queue.status]);

  const loadStats = async () => {
    try {
      const response = await adminAPI.getBroadcastStats();
      if (response.data.success) {
        setStats(response.data.data);
        if (response.data.data.processing) {
          setQueue(prev => ({ ...prev, status: 'processing' }));
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const response = await adminAPI.getBroadcasts({
        page: historyPage + 1,
        limit: historyRowsPerPage
      });
      if (response.data.success) {
        setHistory(response.data.data.broadcasts || []);
        setHistoryTotal(response.data.data.total || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
    }
  };

  const loadQueueStatus = async () => {
    try {
      const response = await adminAPI.getBroadcastQueueStatus();
      if (response.data.success) {
        setQueue(response.data.data);
        if (response.data.data.status !== 'processing') {
          loadStats();
          loadHistory();
        }
      }
    } catch (error) {
      console.error('Ошибка загрузки статуса очереди:', error);
    }
  };

  // Поиск пользователей для рассылки по структуре
  const handleUserSearch = async (query) => {
    setUserSearchQuery(query);
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }

    setSearchingUsers(true);
    try {
      const response = await adminAPI.searchUsersForBroadcast(query);
      if (response.data.success) {
        setUserSearchResults(response.data.data);
      }
    } catch (error) {
      console.error('Ошибка поиска пользователей:', error);
    } finally {
      setSearchingUsers(false);
    }
  };

  // Выбор пользователя для рассылки по структуре
  const handleUserSelect = async (user) => {
    if (!user) {
      setStructureUser(null);
      setStructureUserId(null);
      setStructurePreview(null);
      return;
    }

    setStructureUser(user);
    setStructureUserId(user.id);
    setUserSearchResults([]);
    setUserSearchQuery('');

    // Загружаем превью структуры
    loadStructurePreview(user.id, structureDepth);
  };

  // Загрузка превью структуры
  const loadStructurePreview = async (userId, depth, noStaking = false, noReferrals = false) => {
    if (!userId) return;

    try {
      const response = await adminAPI.getStructurePreview(userId, depth, noStaking, noReferrals);
      if (response.data.success) {
        setStructurePreview(response.data.data);
      }
    } catch (error) {
      console.error('Ошибка загрузки превью структуры:', error);
    }
  };

  // Обновление превью при изменении глубины или фильтров
  useEffect(() => {
    if (structureUserId) {
      loadStructurePreview(structureUserId, structureDepth, filterHasStaking, filterNoReferrals);
    }
  }, [structureDepth, structureUserId, filterHasStaking, filterNoReferrals]);

  // Сброс структуры и фильтров при смене targetType
  useEffect(() => {
    if (targetType !== 'structure') {
      setStructureUser(null);
      setStructureUserId(null);
      setStructurePreview(null);
      setFilterHasStaking(false);
      setFilterNoReferrals(false);
    }
  }, [targetType]);

  // Обработка выбора медиа файла
  const handleMediaSelect = async (file) => {
    if (!file) return;

    // Проверка типа файла
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4'];
    if (!allowedTypes.includes(file.type)) {
      setMediaError('Разрешены только JPG, PNG, GIF и MP4 файлы');
      return;
    }

    // Проверка размера (20MB)
    if (file.size > 20 * 1024 * 1024) {
      setMediaError('Максимальный размер файла: 20MB');
      return;
    }

    setMediaError(null);
    setMediaFile(file);

    // Создаём превью
    const reader = new FileReader();
    reader.onload = (e) => setMediaPreview(e.target.result);
    reader.readAsDataURL(file);

    // Загружаем на сервер для получения file_id
    setUploadingMedia(true);
    try {
      const response = await adminAPI.uploadBroadcastMedia(file);
      if (response.data.success) {
        setMediaType(response.data.data.mediaType);
        setMediaFileId(response.data.data.fileId);
        setMediaLocalPath(response.data.data.localPath);
      } else {
        setMediaError(response.data.error || 'Ошибка загрузки');
        clearMedia();
      }
    } catch (error) {
      console.error('Ошибка загрузки медиа:', error);
      setMediaError(error.response?.data?.error || 'Ошибка загрузки файла');
      clearMedia();
    } finally {
      setUploadingMedia(false);
    }
  };

  // Очистка медиа
  const clearMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setMediaType(null);
    setMediaFileId(null);
    setMediaLocalPath(null);
    setMediaError(null);
  };

  // Определение типа иконки для медиа
  const getMediaIcon = () => {
    if (mediaType === 'video') return <Videocam />;
    if (mediaType === 'animation') return <Gif />;
    return <Image />;
  };

  // Получение чистого текста для подсчёта символов
  const getPlainTextLength = (html) => {
    if (!html) return 0;
    return html.replace(/<[^>]*>/g, '').length;
  };

  // Лимит символов для медиа-сообщений в Telegram
  const MEDIA_CAPTION_LIMIT = 1023;
  const textLength = getPlainTextLength(message);
  const isMediaMode = deliveryChannel !== 'webapp' && mediaFileId;
  const isOverLimit = isMediaMode && textLength > MEDIA_CAPTION_LIMIT;

  const calculateEstimatedTime = (totalUsers) => {
    const batches = Math.ceil(totalUsers / batchSize);
    const timePerBatch = batchSize * delayBetweenMessages;
    const totalTime = (batches * timePerBatch) + ((batches - 1) * delayBetweenBatches);
    return Math.ceil(totalTime / 1000);
  };

  // Умная вставка из буфера обмена (для Telegram и др.)
  const handleSmartPaste = async () => {
    try {
      // Пробуем прочитать clipboard через современный API
      if (navigator.clipboard && navigator.clipboard.read) {
        const clipboardItems = await navigator.clipboard.read();

        for (const item of clipboardItems) {
          // Ищем HTML формат
          if (item.types.includes('text/html')) {
            const blob = await item.getType('text/html');
            const html = await blob.text();

            // Очищаем HTML и добавляем к сообщению
            const cleanHtml = html
              .replace(/<meta[^>]*>/gi, '')
              .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
              .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
              .replace(/<!--[\s\S]*?-->/g, '')
              .replace(/<\/?html[^>]*>/gi, '')
              .replace(/<\/?head[^>]*>/gi, '')
              .replace(/<\/?body[^>]*>/gi, '')
              .replace(/class="[^"]*"/gi, '')
              .replace(/style="[^"]*"/gi, '')
              .replace(/id="[^"]*"/gi, '')
              .trim();

            setMessage(prev => prev + cleanHtml);
            alert('✅ Вставлено с форматированием!');
            return;
          }
        }

        // Если HTML не найден, берём plain text
        const text = await navigator.clipboard.readText();
        setMessage(prev => prev + text);
        alert('ℹ️ Вставлен plain text (форматирование недоступно)');

      } else {
        // Fallback для старых браузеров
        const text = await navigator.clipboard.readText();
        setMessage(prev => prev + text);
        alert('ℹ️ Вставлен plain text');
      }
    } catch (err) {
      console.error('Ошибка вставки:', err);
      alert('❌ Ошибка: ' + err.message + '\n\nПопробуйте Ctrl+V или скопируйте из Telegram Web');
    }
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    if (targetType === 'user' && !targetUser.trim()) {
      alert('Укажите ID или username пользователя');
      return;
    }

    // Валидация для рассылки по структуре
    if (targetType === 'structure' && !structureUserId) {
      alert('Выберите пользователя для рассылки по его структуре');
      return;
    }

    if (targetType === 'structure' && structurePreview && structurePreview.total === 0) {
      alert('У выбранного пользователя нет рефералов в структуре');
      return;
    }

    // Валидация кнопки: оба поля обязательны если включена кнопка
    if (includeButtons && deliveryChannel !== 'webapp') {
      if (!buttonText.trim() || !buttonUrl.trim()) {
        alert('Для кнопки нужно указать и текст, и URL');
        return;
      }
    }

    // Проверка лимита символов для медиа
    if (isOverLimit) {
      alert(`Для сообщений с медиа максимум ${MEDIA_CAPTION_LIMIT} символов. Сейчас: ${textLength}`);
      return;
    }

    setLoading(true);
    setSendingStatus('preparing');

    try {
      const payload = {
        title: title || `Рассылка ${new Date().toLocaleString('ru-RU')}`,
        message,
        priority,
        targetType,
        targetUser: targetType === 'user' ? targetUser : undefined,
        targetGroup: targetType === 'group' ? targetGroup : undefined,
        deliveryChannel,
        includeButtons,
        buttonText: includeButtons ? buttonText : undefined,
        buttonUrl: includeButtons ? buttonUrl : undefined,
        batchSize,
        delayBetweenBatches,
        delayBetweenMessages,
        testMode,
        // Медиа параметры (только для Telegram)
        mediaType: deliveryChannel !== 'webapp' ? mediaType : null,
        mediaFileId: deliveryChannel !== 'webapp' ? mediaFileId : null,
        mediaLocalPath: deliveryChannel !== 'webapp' ? mediaLocalPath : null,
        // Параметры для рассылки по структуре
        structureUserId: targetType === 'structure' ? structureUserId : null,
        structureDepth: targetType === 'structure' ? structureDepth : null,
        // Фильтры для структуры
        filterHasStaking: targetType === 'structure' ? filterHasStaking : false,
        filterNoReferrals: targetType === 'structure' ? filterNoReferrals : false
      };

      const response = await adminAPI.createBroadcast(payload);

      if (response.data.success) {
        setSendingStatus('success');
        setQueue({
          status: 'processing',
          total: response.data.data.totalRecipients,
          sent: 0,
          failed: 0,
          remaining: response.data.data.totalRecipients,
          currentBroadcastId: response.data.data.id
        });

        // Очистка формы
        setTitle('');
        setMessage('');
        setButtonText('');
        setButtonUrl('');
        clearMedia();

        loadStats();
        loadHistory();
      }
    } catch (error) {
      console.error('Ошибка отправки:', error);
      setSendingStatus('error');
    } finally {
      setLoading(false);
      setTimeout(() => setSendingStatus(null), 5000);
    }
  };

  const handleStopQueue = async () => {
    try {
      await adminAPI.stopBroadcastQueue();
      setQueue({ ...queue, status: 'stopped' });
      loadStats();
    } catch (error) {
      console.error('Ошибка остановки очереди:', error);
    }
  };

  const handlePauseQueue = async () => {
    try {
      await adminAPI.pauseBroadcastQueue();
      setQueue({ ...queue, status: 'paused' });
    } catch (error) {
      console.error('Ошибка паузы очереди:', error);
    }
  };

  const handleResumeQueue = async () => {
    try {
      await adminAPI.resumeBroadcastQueue();
      setQueue({ ...queue, status: 'processing' });
    } catch (error) {
      console.error('Ошибка возобновления очереди:', error);
    }
  };

  const handleClearHistory = async () => {
    try {
      await adminAPI.clearBroadcastHistory();
      setClearHistoryDialog(false);
      loadHistory();
      loadStats();
    } catch (error) {
      console.error('Ошибка очистки истории:', error);
    }
  };

  const handleDeleteBroadcast = async (id) => {
    if (!window.confirm('Удалить эту рассылку?')) return;
    try {
      await adminAPI.deleteBroadcast(id);
      loadHistory();
    } catch (error) {
      console.error('Ошибка удаления рассылки:', error);
    }
  };

  // Мемоизированный превью для Telegram
  const telegramPreview = useMemo(() => convertHtmlToTelegramPreview(message), [message]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
        PUSH Рассылки
      </Typography>

      {/* Информация о каналах и приоритетах */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <Alert severity="info">
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              📱 Каналы доставки
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary="• Telegram бот - прямые сообщения"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary="• Веб-приложение - уведомления в игре"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </Alert>
        </Grid>
        <Grid item xs={12} md={6}>
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
              ⚡ Приоритеты уведомлений
            </Typography>
            <List dense sx={{ pl: 2 }}>
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary="• Высокий - блокирующее модальное окно"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary="• Обычный/Низкий - в колокольчик"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </Alert>
        </Grid>
      </Grid>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">
                Всего пользователей
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold' }}>
                {formatNumber(stats.totalUsers || stats.total_users || 0)}
              </Typography>
              <Typography color="white" variant="caption">
                Активных: {formatNumber(stats.activeUsers || stats.active_users || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">
                Отправлено сегодня
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold' }}>
                {formatNumber(stats.sentToday || stats.sent_today || 0)}
              </Typography>
              <Typography color="white" variant="caption">
                Рассылок
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">
                За неделю
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold' }}>
                {formatNumber(stats.sentThisWeek || stats.sent_this_week || 0)}
              </Typography>
              <Typography color="white" variant="caption">
                Рассылок
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{
            background: queue.status === 'processing'
              ? 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <CardContent>
              <Typography color="white" gutterBottom variant="body2">
                Статус очереди
              </Typography>
              <Typography color="white" variant="h5" sx={{ fontWeight: 'bold' }}>
                {queue.status === 'processing' ? 'Отправка...' :
                 queue.status === 'paused' ? 'Пауза' : 'Готов'}
              </Typography>
              {queue.status === 'processing' && (
                <Typography color="white" variant="caption">
                  {queue.sent}/{queue.total} отправлено
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Статус отправки */}
      {queue.status === 'processing' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            <Typography variant="h6">
              Идет рассылка...
            </Typography>
            <Box>
              {queue.status === 'paused' ? (
                <IconButton onClick={handleResumeQueue} color="success">
                  <PlayArrow />
                </IconButton>
              ) : (
                <IconButton onClick={handlePauseQueue} color="warning">
                  <Pause />
                </IconButton>
              )}
              <IconButton onClick={handleStopQueue} color="error">
                <Stop />
              </IconButton>
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Прогресс</Typography>
              <Typography variant="h6">{queue.sent} / {queue.total}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Ошибок</Typography>
              <Typography variant="h6" color="error">{queue.failed}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Осталось</Typography>
              <Typography variant="h6">{queue.remaining}</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <Typography variant="body2" color="text.secondary">Осталось времени</Typography>
              <Typography variant="h6">~{Math.floor(calculateEstimatedTime(queue.remaining) / 60)}м</Typography>
            </Grid>
          </Grid>

          <LinearProgress
            variant="determinate"
            value={queue.total > 0 ? (queue.sent / queue.total) * 100 : 0}
            sx={{ mt: 2, height: 8, borderRadius: 1 }}
          />
        </Paper>
      )}

      {/* Форма отправки */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Новая рассылка
        </Typography>

        <Grid container spacing={3}>
          {/* Левая колонка - редактор */}
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Заголовок (опционально)"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Заголовок рассылки..."
              disabled={queue.status === 'processing'}
              sx={{ mb: 2 }}
            />

            <Typography variant="body2" sx={{ mb: 1 }}>Текст сообщения</Typography>
            <Box sx={{
              mb: 2,
              // Стили для тёмной темы React Quill
              '& .ql-toolbar': {
                bgcolor: '#2d2d3d',
                borderColor: '#444',
                borderTopLeftRadius: '4px',
                borderTopRightRadius: '4px',
                '& .ql-stroke': { stroke: '#e0e0e0' },
                '& .ql-fill': { fill: '#e0e0e0' },
                '& .ql-picker': { color: '#e0e0e0' },
                '& .ql-picker-label': { color: '#e0e0e0' },
                '& .ql-picker-options': {
                  bgcolor: '#2d2d3d',
                  borderColor: '#444',
                },
                '& button:hover .ql-stroke': { stroke: '#90caf9' },
                '& button:hover .ql-fill': { fill: '#90caf9' },
                '& button.ql-active .ql-stroke': { stroke: '#90caf9' },
                '& button.ql-active .ql-fill': { fill: '#90caf9' },
              },
              '& .ql-container': {
                minHeight: '150px',
                bgcolor: '#1e1e2e',
                borderColor: '#444',
                borderBottomLeftRadius: '4px',
                borderBottomRightRadius: '4px',
                fontSize: '14px',
              },
              '& .ql-editor': {
                minHeight: '150px',
                color: '#e0e0e0',
                '&.ql-blank::before': {
                  color: '#888',
                  fontStyle: 'normal',
                },
              },
              '& .ql-editor a': {
                color: '#90caf9',
              },
            }}>
              <ReactQuill
                theme="snow"
                value={message}
                onChange={setMessage}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Введите текст сообщения..."
                readOnly={queue.status === 'processing'}
              />
            </Box>

            {/* Кнопка умной вставки */}
            <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ContentPaste />}
                onClick={handleSmartPaste}
                disabled={queue.status === 'processing'}
                sx={{ color: '#90caf9', borderColor: '#90caf9' }}
              >
                Вставить из буфера
              </Button>
              <Typography variant="caption" sx={{ color: '#666' }}>
                Копируй из Telegram → жми эту кнопку
              </Typography>
            </Box>

            {/* Превью */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#1a1a2e' }}>
                  <Typography variant="body2" sx={{ mb: 1, color: '#999' }}>
                    Превью Web
                  </Typography>
                  {/* 🔒 SECURITY: Санитизация HTML для защиты от XSS */}
                  <Box
                    sx={{ color: '#fff', fontSize: '14px', lineHeight: 1.6 }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(message, { ALLOWED_TAGS: ['b', 'i', 'u', 's', 'a', 'br', 'p', 'ul', 'ol', 'li'], ALLOWED_ATTR: ['href', 'target'] }) }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#0088cc' }}>
                  <Typography variant="body2" sx={{ mb: 1, color: 'rgba(255,255,255,0.7)' }}>
                    Превью Telegram
                  </Typography>
                  {/* 🔒 SECURITY: Санитизация HTML для защиты от XSS */}
                  <Box
                    sx={{ color: '#fff', fontSize: '14px', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(telegramPreview, { ALLOWED_TAGS: ['b', 'i', 'u', 's', 'a', 'br'], ALLOWED_ATTR: ['href', 'target'] }) }}
                  />
                </Paper>
              </Grid>
            </Grid>

            {/* Кнопка (только для Telegram) */}
            {deliveryChannel !== 'webapp' && (
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeButtons}
                      onChange={(e) => setIncludeButtons(e.target.checked)}
                      disabled={queue.status === 'processing'}
                    />
                  }
                  label="Добавить кнопку (только Telegram)"
                />

                {includeButtons && (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Текст кнопки"
                        value={buttonText}
                        onChange={(e) => setButtonText(e.target.value)}
                        disabled={queue.status === 'processing'}
                        error={includeButtons && !buttonText.trim()}
                        helperText={includeButtons && !buttonText.trim() ? 'Обязательное поле' : ''}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="URL кнопки"
                        value={buttonUrl}
                        onChange={(e) => setButtonUrl(e.target.value)}
                        placeholder="example.com или https://..."
                        disabled={queue.status === 'processing'}
                        error={includeButtons && !buttonUrl.trim()}
                        helperText={includeButtons && !buttonUrl.trim() ? 'Обязательное поле' : 'https:// добавится автоматически'}
                      />
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {/* Загрузка медиа (только для Telegram) */}
            {deliveryChannel !== 'webapp' && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Image fontSize="small" />
                  Медиа (только Telegram)
                </Typography>

                {!mediaFile ? (
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: mediaError ? 'error.main' : '#444',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: queue.status === 'processing' || uploadingMedia ? 'not-allowed' : 'pointer',
                      bgcolor: '#1e1e2e',
                      '&:hover': {
                        borderColor: queue.status === 'processing' ? '#444' : '#667eea',
                        bgcolor: queue.status === 'processing' ? '#1e1e2e' : '#252538'
                      }
                    }}
                    onClick={() => {
                      if (queue.status !== 'processing' && !uploadingMedia) {
                        document.getElementById('media-upload').click();
                      }
                    }}
                  >
                    <input
                      type="file"
                      id="media-upload"
                      hidden
                      accept="image/jpeg,image/png,image/gif,video/mp4"
                      onChange={(e) => handleMediaSelect(e.target.files[0])}
                    />
                    <CloudUpload sx={{ fontSize: 40, color: '#666', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      Нажмите или перетащите файл
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      JPG, PNG, GIF, MP4 (до 20MB)
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    {uploadingMedia ? (
                      <Box sx={{ textAlign: 'center', p: 3 }}>
                        <LinearProgress sx={{ mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Загрузка на сервер...
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 2,
                        bgcolor: '#1e1e2e',
                        borderRadius: 2,
                        border: '1px solid #444'
                      }}>
                        {/* Превью */}
                        {mediaType === 'video' ? (
                          <Box sx={{
                            width: 80,
                            height: 80,
                            bgcolor: '#000',
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Videocam sx={{ fontSize: 40, color: '#666' }} />
                          </Box>
                        ) : (
                          <Box
                            component="img"
                            src={mediaPreview}
                            sx={{
                              width: 80,
                              height: 80,
                              objectFit: 'cover',
                              borderRadius: 1
                            }}
                          />
                        )}

                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getMediaIcon()}
                            {mediaFile.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {(mediaFile.size / 1024 / 1024).toFixed(2)} MB •{' '}
                            {mediaType === 'photo' ? 'Фото' : mediaType === 'video' ? 'Видео' : 'GIF'}
                          </Typography>
                          {mediaFileId && (
                            <Typography variant="caption" color="success.main" display="block">
                              Загружено на сервер
                            </Typography>
                          )}
                        </Box>

                        <IconButton
                          onClick={clearMedia}
                          disabled={queue.status === 'processing'}
                          color="error"
                        >
                          <Close />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                )}

                {mediaError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {mediaError}
                  </Alert>
                )}
              </Box>
            )}

            {/* Счётчик символов */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="caption"
                color={isOverLimit ? 'error' : 'text.secondary'}
                sx={{ fontWeight: isOverLimit ? 'bold' : 'normal' }}
              >
                {textLength} символов
                {isMediaMode && (
                  <span> / {MEDIA_CAPTION_LIMIT} (лимит для медиа)</span>
                )}
              </Typography>
              {isOverLimit && (
                <Chip
                  label={`-${textLength - MEDIA_CAPTION_LIMIT}`}
                  color="error"
                  size="small"
                />
              )}
            </Box>
          </Grid>

          {/* Правая колонка - настройки */}
          <Grid item xs={12} md={4}>
            {/* Приоритет */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Приоритет</InputLabel>
              <Select
                value={priority}
                label="Приоритет"
                onChange={(e) => setPriority(e.target.value)}
                disabled={queue.status === 'processing'}
              >
                <MenuItem value="high">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PriorityHigh fontSize="small" color="error" />
                    Высокий (модальное окно)
                  </Box>
                </MenuItem>
                <MenuItem value="normal">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Notifications fontSize="small" />
                    Обычный (колокольчик)
                  </Box>
                </MenuItem>
                <MenuItem value="low">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Notifications fontSize="small" color="disabled" />
                    Низкий (колокольчик)
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Канал доставки */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Канал доставки</InputLabel>
              <Select
                value={deliveryChannel}
                label="Канал доставки"
                onChange={(e) => setDeliveryChannel(e.target.value)}
                disabled={queue.status === 'processing'}
              >
                <MenuItem value="telegram">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Campaign fontSize="small" />
                    Только Telegram
                  </Box>
                </MenuItem>
                <MenuItem value="webapp">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NotificationsActive fontSize="small" />
                    Только веб-приложение
                  </Box>
                </MenuItem>
                <MenuItem value="both">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Send fontSize="small" />
                    Telegram + веб
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Целевая аудитория */}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Кому отправить</InputLabel>
              <Select
                value={targetType}
                label="Кому отправить"
                onChange={(e) => setTargetType(e.target.value)}
                disabled={queue.status === 'processing'}
              >
                <MenuItem value="all">Всем пользователям</MenuItem>
                <MenuItem value="group">Группе пользователей</MenuItem>
                <MenuItem value="user">Конкретному пользователю</MenuItem>
                <MenuItem value="structure">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountTree fontSize="small" color="primary" />
                    По структуре пользователя
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {targetType === 'user' && (
              <TextField
                fullWidth
                label="ID или username"
                value={targetUser}
                onChange={(e) => setTargetUser(e.target.value)}
                sx={{ mb: 2 }}
                disabled={queue.status === 'processing'}
              />
            )}

            {targetType === 'group' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Группа</InputLabel>
                <Select
                  value={targetGroup}
                  label="Группа"
                  onChange={(e) => setTargetGroup(e.target.value)}
                  disabled={queue.status === 'processing'}
                >
                  <MenuItem value="active">Активные (за 7 дней)</MenuItem>
                  <MenuItem value="inactive">Неактивные</MenuItem>
                  <MenuItem value="vip">VIP пользователи</MenuItem>
                  <MenuItem value="leaders">Лидеры</MenuItem>
                  <MenuItem value="newbies">Новички</MenuItem>
                  <MenuItem value="stakers">Со стейкингом</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Рассылка по структуре пользователя */}
            {targetType === 'structure' && (
              <Box sx={{ mb: 2 }}>
                {/* Поиск пользователя */}
                <TextField
                  fullWidth
                  label="Поиск лидера (username или telegram_id)"
                  value={userSearchQuery}
                  onChange={(e) => handleUserSearch(e.target.value)}
                  disabled={queue.status === 'processing' || structureUser}
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />
                  }}
                />

                {/* Результаты поиска */}
                {userSearchResults.length > 0 && !structureUser && (
                  <Paper sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
                    <List dense>
                      {userSearchResults.map((user) => (
                        <ListItem
                          key={user.id}
                          button
                          onClick={() => handleUserSelect(user)}
                          sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                        >
                          <ListItemText
                            primary={`@${user.username || user.first_name || 'Без имени'}`}
                            secondary={`ID: ${user.telegram_id} • Рефералов: ${user.direct_referrals}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                )}

                {searchingUsers && (
                  <Typography variant="caption" color="text.secondary">
                    Поиск...
                  </Typography>
                )}

                {/* Выбранный пользователь */}
                {structureUser && (
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      icon={<Person />}
                      label={`@${structureUser.username || structureUser.first_name} • ${structureUser.direct_referrals} прямых рефералов`}
                      onDelete={() => handleUserSelect(null)}
                      color="primary"
                      sx={{ mb: 2 }}
                    />

                    {/* Слайдер глубины */}
                    <Typography variant="body2" gutterBottom>
                      Глубина уровней: {structureDepth}
                    </Typography>
                    <Slider
                      value={structureDepth}
                      onChange={(e, val) => setStructureDepth(val)}
                      min={1}
                      max={5}
                      step={1}
                      marks={[
                        { value: 1, label: '1' },
                        { value: 2, label: '2' },
                        { value: 3, label: '3' },
                        { value: 4, label: '4' },
                        { value: 5, label: '5' }
                      ]}
                      disabled={queue.status === 'processing'}
                      sx={{ mb: 2 }}
                    />

                    {/* Фильтры для структуры */}
                    <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
                      Фильтры:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterHasStaking}
                            onChange={(e) => setFilterHasStaking(e.target.checked)}
                            disabled={queue.status === 'processing'}
                            size="small"
                          />
                        }
                        label="Со стейкингом"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={filterNoReferrals}
                            onChange={(e) => setFilterNoReferrals(e.target.checked)}
                            disabled={queue.status === 'processing'}
                            size="small"
                          />
                        }
                        label="Без рефералов"
                      />
                    </Box>

                    {/* Превью получателей */}
                    {structurePreview && (
                      <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccountTree fontSize="small" />
                          Получатели по уровням
                        </Typography>
                        {Object.entries(structurePreview.byLevel).map(([level, count]) => (
                          <Box key={level} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ width: 80 }}>
                              Уровень {level.replace('level', '')}:
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={structurePreview.total > 0 ? (count / structurePreview.total) * 100 : 0}
                              sx={{ flex: 1, mx: 1, height: 8, borderRadius: 1 }}
                            />
                            <Typography variant="body2" sx={{ width: 50, textAlign: 'right' }}>
                              {count}
                            </Typography>
                          </Box>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6" color="primary">
                          Всего: {formatNumber(structurePreview.total)} получателей
                        </Typography>
                        {structurePreview.total === 0 && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            У пользователя нет рефералов на выбранных уровнях
                          </Alert>
                        )}
                      </Paper>
                    )}
                  </Box>
                )}
              </Box>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={testMode}
                  onChange={(e) => setTestMode(e.target.checked)}
                  disabled={queue.status === 'processing'}
                />
              }
              label="Тестовый режим (только админам)"
            />
          </Grid>
        </Grid>

        {/* Настройки защиты от спама */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            Настройки отправки
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" gutterBottom>
                Размер батча: {batchSize}
              </Typography>
              <Slider
                value={batchSize}
                onChange={(e, val) => setBatchSize(val)}
                min={10}
                max={50}
                step={5}
                marks
                disabled={queue.status === 'processing'}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" gutterBottom>
                Задержка между батчами: {delayBetweenBatches}мс
              </Typography>
              <Slider
                value={delayBetweenBatches}
                onChange={(e, val) => setDelayBetweenBatches(val)}
                min={500}
                max={5000}
                step={500}
                marks
                disabled={queue.status === 'processing'}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" gutterBottom>
                Задержка между сообщениями: {delayBetweenMessages}мс
              </Typography>
              <Slider
                value={delayBetweenMessages}
                onChange={(e, val) => setDelayBetweenMessages(val)}
                min={30}
                max={200}
                step={10}
                marks
                disabled={queue.status === 'processing'}
              />
            </Grid>
          </Grid>

          {targetType === 'all' && stats.totalUsers > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Примерное время отправки: ~{Math.floor(calculateEstimatedTime(stats.totalUsers || stats.total_users) / 60)} минут
            </Alert>
          )}
        </Box>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setTitle('');
              setMessage('');
              setButtonText('');
              setButtonUrl('');
              clearMedia();
            }}
            disabled={queue.status === 'processing'}
          >
            Очистить
          </Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={handleSend}
            disabled={
              !message.trim() ||
              queue.status === 'processing' ||
              loading ||
              uploadingMedia ||
              isOverLimit ||
              (includeButtons && deliveryChannel !== 'webapp' && (!buttonText.trim() || !buttonUrl.trim()))
            }
            sx={{ bgcolor: '#6366f1' }}
          >
            {uploadingMedia ? 'Загрузка медиа...' : testMode ? 'Отправить тест' : 'Начать рассылку'}
          </Button>
        </Box>

        {sendingStatus === 'success' && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Рассылка запущена!
          </Alert>
        )}
        {sendingStatus === 'error' && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Ошибка запуска рассылки.
          </Alert>
        )}
      </Paper>

      {/* История рассылок */}
      <Paper sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography variant="h6">
            История рассылок
          </Typography>
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => setClearHistoryDialog(true)}
            disabled={history.length === 0}
          >
            Очистить историю
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Сообщение</TableCell>
                <TableCell>Приоритет</TableCell>
                <TableCell>Кому</TableCell>
                <TableCell>Отправлено</TableCell>
                <TableCell>Заблокировали</TableCell>
                <TableCell>Прочитано</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Время</TableCell>
                <TableCell>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id}</TableCell>
                  <TableCell sx={{ maxWidth: 250 }}>
                    <Typography variant="body2" noWrap>
                      {item.title || item.message?.replace(/<[^>]*>/g, '').substring(0, 50)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.priority === 'high' ? 'Высокий' : item.priority === 'low' ? 'Низкий' : 'Обычный'}
                      color={item.priority === 'high' ? 'error' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {item.target_type === 'all' ? 'Всем' :
                     item.target_type === 'group' ? `Группа: ${item.target_group}` :
                     item.target_type === 'structure' ? (
                       item.structure_username
                         ? `Структура: @${item.structure_username}`
                         : 'По структуре'
                     ) :
                     item.target_type === 'user' ? `Пользователь: ${item.target_user_id}` :
                     item.target_type}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" color="success.main">
                        ✓ {item.sent_count}
                      </Typography>
                      {item.failed_count > 0 && (
                        <Typography variant="caption" color="error">
                          ✗ {item.failed_count}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {(item.blocked_count || 0) > 0 ? (
                      <Typography variant="body2" color="error">
                        🚫 {item.blocked_count}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="success.main">
                      {item.read_count || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.status === 'completed' ? 'Завершено' :
                             (item.status === 'processing' || item.status === 'in_progress') ? 'В процессе' :
                             item.status === 'stopped' ? 'Остановлено' : 'Ожидание'}
                      color={item.status === 'completed' ? 'success' :
                             (item.status === 'processing' || item.status === 'in_progress') ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(item.created_at).toLocaleString('ru-RU')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteBroadcast(item.id)}
                      disabled={item.status === 'processing'}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {history.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography color="text.secondary">
                      История рассылок пуста
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={historyTotal}
          page={historyPage}
          onPageChange={(e, newPage) => {
            setHistoryPage(newPage);
            loadHistory();
          }}
          rowsPerPage={historyRowsPerPage}
          onRowsPerPageChange={(e) => {
            setHistoryRowsPerPage(parseInt(e.target.value, 10));
            setHistoryPage(0);
            loadHistory();
          }}
          labelRowsPerPage="Строк на странице:"
        />
      </Paper>

      {/* Диалог подтверждения очистки истории */}
      <Dialog open={clearHistoryDialog} onClose={() => setClearHistoryDialog(false)}>
        <DialogTitle>Очистить историю рассылок?</DialogTitle>
        <DialogContent>
          <Typography>
            Все записи о рассылках будут удалены. Это действие нельзя отменить.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearHistoryDialog(false)}>
            Отмена
          </Button>
          <Button onClick={handleClearHistory} color="error" variant="contained">
            Очистить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PushAlerts;
